import {
    CustomFilter,
    Dashboard,
    Folder,
    List,
    ListView,
    ListViewColumn,
    Member,
    Memo,
    Note,
    Pipeline,
    Report,
    ReportComponent,
    Team,
    Workspace,
} from '@/types';

export type ResourceReference = {
    id: string;
};

function toRecord(value: unknown): Record<string, unknown> | undefined {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined;
}

function toResourceReference(value: unknown): ResourceReference | null | undefined {
    if (value === null) {
        return null;
    }

    const record = toRecord(value);
    return typeof record?.id === 'string' && record.id.length > 0 ? { id: record.id } : undefined;
}

function dashboardFields(input: Partial<Dashboard>) {
    const record = toRecord(input);

    return {
        archived: input.archived,
        description: input.description,
        favorited: input.favorited,
        folder: toResourceReference(record?.folder),
        locked: input.locked,
        name: input.name,
        pipelineColumn: toResourceReference(record?.pipelineColumn),
        publiclyVisible: input.publiclyVisible,
        publicMetadataVisibility: input.publicMetadataVisibility,
        visibility: input.visibility,
    };
}

function pipelineFields(input: Partial<Pipeline>) {
    return {
        folder: toResourceReference(input.folder),
        isArchived: input.isArchived,
        isFavorite: input.isFavorite,
        isLocked: input.isLocked,
        name: input.name,
        team: toResourceReference(input.team),
        type: input.type,
        view: input.view,
        visibility: input.visibility,
    };
}

export function toDashboardMutationInput(input: Partial<Dashboard>) {
    return dashboardFields(input);
}

export function toFolderMutationInput(input: Partial<Folder>) {
    return {
        archived: input.archived,
        favorited: input.favorited,
        locked: input.locked,
        name: input.name,
        type: input.type,
        visibility: input.visibility,
    };
}

export function toListMutationInput(input: Partial<List>) {
    const record = toRecord(input);

    return {
        archived: input.archived,
        autopopulateColumns: input.autopopulateColumns,
        primaryColumnKey: input.primaryColumnKey,
        dataSources: input.dataSources?.map((dataSource) => ({
            connectedAccount: dataSource.connectedAccount
                ? {
                      id: dataSource.connectedAccount.id,
                      type: dataSource.connectedAccount.type,
                  }
                : undefined,
            id: dataSource.id,
        })),
        description: input.description,
        favorited: input.favorited,
        folder: toResourceReference(record?.folder),
        icon: input.icon,
        iconBackgroundColor: input.iconBackgroundColor,
        locked: input.locked,
        name: input.name,
        pipelineColumn: toResourceReference(record?.pipelineColumn),
        publiclyVisible: input.publiclyVisible,
        type: input.type,
        visibility: input.visibility,
    };
}

export function toMemoMutationInput(input: Partial<Memo>) {
    return {
        archived: input.archived,
        avatar: input.avatar,
        backgroundColor: input.backgroundColor,
        banner: input.banner,
        bannerVisible: input.bannerVisible,
        body: input.body,
        documentType: input.documentType,
        emoji: input.emoji,
        favorited: input.favorited,
        folder: toResourceReference(input.folder),
        foregroundColor: input.foregroundColor,
        icon: input.icon,
        locked: input.locked,
        pipelineColumn: toResourceReference(input.pipelineColumn),
        publiclyVisible: input.publiclyVisible,
        publicMetadataVisibility: input.publicMetadataVisibility,
        sourcesVisible: input.sourcesVisible,
        title: input.title,
        tocVisible: input.tocVisible,
        type: input.type,
    };
}

export function toPipelineCreateInput(input: Partial<Pipeline>) {
    return {
        ...pipelineFields(input),
        columns: input.columns?.map((column) => ({
            color: column.color,
            index: column.index,
            name: column.name,
        })),
    };
}

export function toPipelineUpdateInput(input: Partial<Pipeline>) {
    return pipelineFields(input);
}

export function toReportMutationInput(input: Partial<Report>) {
    return {
        archived: input.archived,
        closed: input.closed,
        dashboard: toResourceReference(input.dashboard),
        description: input.description,
        favorited: input.favorited,
        locked: input.locked,
        name: input.name,
        orderIndex: input.orderIndex,
        publiclyVisible: input.publiclyVisible,
        schedulingSettings: input.schedulingSettings,
        type: input.type,
        visibility: input.visibility,
    };
}

function reportComponentFields(input: Partial<ReportComponent>) {
    return {
        archived: input.archived,
        configuration: input.configuration,
        description: input.description,
        embedURL: input.embedURL,
        favorited: input.favorited,
        hidden: input.hidden,
        index: input.index,
        locked: input.locked,
        name: input.name,
        publiclyVisible: input.publiclyVisible,
        tileHeight: input.tileHeight,
        tileWidth: input.tileWidth,
        tileX: input.tileX,
        tileY: input.tileY,
        type: input.type,
    };
}

export function toReportComponentCreateInput(input: Partial<ReportComponent>) {
    return {
        ...reportComponentFields(input),
        report: toResourceReference(input.report),
    };
}

export function toReportComponentUpdateInput(input: Partial<ReportComponent>) {
    return reportComponentFields(input);
}

function listViewFields(input: Partial<ListView>) {
    return {
        archived: input.archived,
        favorited: input.favorited,
        filters: input.filters,
        hidden: input.hidden,
        locked: input.locked,
        name: input.name,
        order: input.order,
        stages: input.stages,
        type: input.type,
        visibility: input.visibility,
    };
}

export function toListViewCreateInput(input: Partial<ListView>) {
    return {
        ...listViewFields(input),
        list: toResourceReference(input.list),
    };
}

export function toListViewUpdateInput(input: Partial<ListView>) {
    return listViewFields(input);
}

function listViewColumnFields(input: Partial<ListViewColumn>) {
    return {
        dataType: input.dataType,
        isHidden: input.isHidden,
        key: input.key,
        label: input.label,
        order: input.order,
    };
}

export function toListViewColumnCreateInput(input: Partial<ListViewColumn>) {
    return {
        ...listViewColumnFields(input),
        view: toResourceReference(input.view),
    };
}

export function toListViewColumnUpdateInput(input: Partial<ListViewColumn>) {
    return listViewColumnFields(input);
}

export function toNoteMutationInput(input: Partial<Note>) {
    return {
        archived: input.archived,
        associations: input.associations,
        body: input.body,
        favorited: input.favorited,
        locked: input.locked,
        title: input.title,
        type: input.type,
        visibility: input.visibility,
    };
}

function teamFields(input: Partial<Team>) {
    return {
        background: input.background,
        description: input.description,
        emoji: input.emoji,
        handle: input.handle,
        name: input.name,
        visibility: input.visibility,
    };
}

export function toTeamCreateInput(input: Partial<Team>) {
    return teamFields(input);
}

export function toTeamUpdateInput(input: Partial<Team>) {
    return {
        ...teamFields(input),
        archived: input.archived,
        favorited: input.favorited,
        locked: input.locked,
    };
}

export function toWorkspaceMutationInput(input: Partial<Workspace>) {
    return {
        avatar: input.avatar,
        dateFormat: input.dateFormat,
        emoji: input.emoji,
        financialValueDisplay: input.financialValueDisplay,
        fiscalYearStartMonth: input.fiscalYearStartMonth,
        name: input.name,
        reportingCurrency: input.reportingCurrency,
        subdomain: input.subdomain,
        weekStartDay: input.weekStartDay,
        workspaceTimeZone: input.workspaceTimeZone,
    };
}

export function toMemberMutationInput(input: Partial<Member>) {
    return {
        status: input.status,
    };
}

export function toCustomFilterMutationInput(input: Partial<CustomFilter>) {
    return {
        description: input.description,
        favorited: input.favorited,
        query: input.query,
        title: input.title,
        type: input.type,
        visibility: input.visibility,
    };
}
